// Exports: TaskBlock
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { CgPushRight } from "react-icons/cg";
import CalendarModal from "screens/Project Dashboard/Tasks/CalendarModal";
import {
  mapBackground,
  mapDurationTextClass,
  renderDays,
  renderTextForDue,
} from "utils/tasks/taskDateUtils";
import {
  getTaskDisplayStatus,
  isSubtaskCompleted,
  priorityToNumeric,
} from "utils/tasks/taskHelpers";
import AddTaskMemberModal from "./AddTaskMemberModal";
import PriorityBadge from "./PriorityBadge";
import StatusBadge from "./StatusBadge";
import SubtaskRow from "./SubtaskRow";

const getSubtaskOrderValue = (subtask) => {
  const numericValue = Number(
    subtask?.subtask_order_index ?? subtask?.subtaskOrder,
  );
  return Number.isFinite(numericValue) ? numericValue : null;
};

const hasPersistedSubtaskOrder = (subtasks = []) =>
  subtasks.some((subtask) => getSubtaskOrderValue(subtask) !== null);

const TaskBlockComponent = ({
  tg,
  task,
  siteMembers,
  taskAuthority,
  bulkEditGroupId,
  selectedTaskIds,
  toggleTaskSelected,
  onToggleTask,
  setSelectedTaskGroup,
  setSelectedTask,
  setSelectedSubtask,
  editingTaskId,
  onTaskEditStart,
  onTaskEditCancel,
  onTaskEditSubmit,
  editingSubId,
  onSubEditStart,
  onSubEditCancel,
  onSubEditSubmit,
  showAddSubtaskModal,
  setShowAddSubtaskModal,
  setShowAddTaskModal,
  handleAddSubtask,
  onAddSubtaskClick,
  openStatusDropdown,
  setOpenStatusDropdown,
  openPriorityDropdown,
  setOpenPriorityDropdown,
  statusOptions,
  onTaskStatusChange,
  onTaskPriorityChange,
  handleTaskDateChange,
  handleSubtaskDateChange,
  onToggleSubComplete,
  getInitials,
  preserveScroll,
  refetchTasks,
  refetchSubtasks,
  mergeGroupAllocatedMembers,
  closeAddForms,
  canReorderTask,
  isDragging,
  dropIndicatorPosition,
  onReorderDragStart,
  onReorderDragEnd,
  onReorderDragOver,
  onReorderDrop,
  onSubtaskReorder,
  taskReorderDisabled,
}) => {
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [subtaskInputValue, setSubtaskInputValue] = useState("");
  const [draggingSubtaskId, setDraggingSubtaskId] = useState(null);
  const [subtaskDropIndicator, setSubtaskDropIndicator] = useState(null);
  const pendingTaskNameRef = useRef(task.name);
  const inputRef = useRef(null);
  const draggedSubtaskIdRef = useRef(null);
  const rowDropShadow =
    dropIndicatorPosition === "before"
      ? "inset 0 2px 0 0 #3B0069"
      : dropIndicatorPosition === "after"
        ? "inset 0 -2px 0 0 #3B0069"
        : undefined;
  const orderedSubtasks = useMemo(() => {
    const subtasks = [...(task.subtasks || [])];

    if (hasPersistedSubtaskOrder(subtasks)) {
      return subtasks.sort((left, right) => {
        const leftOrder = getSubtaskOrderValue(left);
        const rightOrder = getSubtaskOrderValue(right);

        if (leftOrder == null && rightOrder == null) return 0;
        if (leftOrder == null) return 1;
        if (rightOrder == null) return -1;

        return leftOrder - rightOrder;
      });
    }

    return subtasks.sort((left, right) => {
      const leftDone = isSubtaskCompleted(left);
      const rightDone = isSubtaskCompleted(right);
      if (leftDone && !rightDone) return 1;
      if (!leftDone && rightDone) return -1;
      return 0;
    });
  }, [task.subtasks]);
  const orderedSubtasksRef = useRef(orderedSubtasks);
  const canReorderSubtasks =
    !taskReorderDisabled &&
    typeof onSubtaskReorder === "function" &&
    orderedSubtasks.length > 1;

  useEffect(() => {
    if (editingTaskId !== task.id) return;
    pendingTaskNameRef.current = task.name;
    if (inputRef.current) {
      inputRef.current.value = task.name;
    }
  }, [editingTaskId, task.id, task.name]);

  useEffect(() => {
    orderedSubtasksRef.current = orderedSubtasks;
  }, [orderedSubtasks]);

  const resetSubtaskDrag = useCallback(() => {
    draggedSubtaskIdRef.current = null;
    setDraggingSubtaskId(null);
    setSubtaskDropIndicator(null);
  }, []);

  useEffect(() => {
    if (!draggedSubtaskIdRef.current) return;

    const draggedSubtaskStillExists = orderedSubtasks.some(
      (subtask) => String(subtask.id) === String(draggedSubtaskIdRef.current),
    );

    if (draggedSubtaskStillExists) return;
    resetSubtaskDrag();
  }, [orderedSubtasks, resetSubtaskDrag]);

  useEffect(() => {
    if (!task.expanded || orderedSubtasks.length === 0) return;

    let frameId = window.requestAnimationFrame(() => {
      frameId = window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event("resize"));
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [orderedSubtasks, task.expanded, task.id, task.torder_index]);

  const openTaskDetails = () => {
    closeAddForms();
    setSelectedTask(task);
    setSelectedTaskGroup(null);
    setSelectedSubtask(null);
  };

  const submitTaskName = () =>
    preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, () =>
      onTaskEditSubmit(pendingTaskNameRef.current),
    );

  const handleAddSubtaskSubmit = async (event) => {
    event.preventDefault();
    const subtaskName = subtaskInputValue.trim();
    if (!subtaskName) return;

    await preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, async () => {
      await handleAddSubtask({
        tgId: tg.id,
        taskId: task.id,
        task_id: task.id,
        subtask_name: subtaskName,
      });
      setSubtaskInputValue("");
      // setShowAddSubtaskModal(null);
    });
  };

  const getDropPosition = useCallback((event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return event.clientY < bounds.top + bounds.height / 2 ? "before" : "after";
  }, []);

  const handleSubtaskDragStart = useCallback(
    (event, subtaskId) => {
      if (!canReorderSubtasks) {
        event.preventDefault();
        return;
      }

      event.stopPropagation();
      draggedSubtaskIdRef.current = subtaskId;
      setDraggingSubtaskId(subtaskId);
      setSubtaskDropIndicator(null);

      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(
          "application/x-subtask-id",
          String(subtaskId),
        );
      }
    },
    [canReorderSubtasks],
  );

  const handleSubtaskDragOver = useCallback(
    (event, subtaskId) => {
      const draggedSubtaskId = draggedSubtaskIdRef.current;

      if (!canReorderSubtasks || !draggedSubtaskId) return;
      if (String(draggedSubtaskId) === String(subtaskId)) return;

      event.preventDefault();
      event.stopPropagation();

      const position = getDropPosition(event);
      setSubtaskDropIndicator((current) =>
        current?.subtaskId === subtaskId && current?.position === position
          ? current
          : {
              subtaskId,
              position,
            },
      );

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
    },
    [canReorderSubtasks, getDropPosition],
  );

  const handleSubtaskDrop = useCallback(
    (event, targetSubtaskId) => {
      if (!canReorderSubtasks) return;

      event.preventDefault();
      event.stopPropagation();

      const draggedSubtaskId =
        draggedSubtaskIdRef.current ||
        event.dataTransfer?.getData("application/x-subtask-id");

      if (
        !draggedSubtaskId ||
        String(draggedSubtaskId) === String(targetSubtaskId)
      ) {
        resetSubtaskDrag();
        return;
      }

      const currentSubtasks = orderedSubtasksRef.current;
      const sourceIndex = currentSubtasks.findIndex(
        (subtask) => String(subtask.id) === String(draggedSubtaskId),
      );
      const targetIndex = currentSubtasks.findIndex(
        (subtask) => String(subtask.id) === String(targetSubtaskId),
      );

      if (sourceIndex === -1 || targetIndex === -1) {
        resetSubtaskDrag();
        return;
      }

      const position =
        subtaskDropIndicator?.subtaskId === targetSubtaskId
          ? subtaskDropIndicator.position
          : getDropPosition(event);
      const reorderedSubtasks = [...currentSubtasks];
      const [movedSubtask] = reorderedSubtasks.splice(sourceIndex, 1);
      let insertionIndex = targetIndex;

      if (sourceIndex < targetIndex) {
        insertionIndex -= 1;
      }
      if (position === "after") {
        insertionIndex += 1;
      }

      reorderedSubtasks.splice(insertionIndex, 0, movedSubtask);
      onSubtaskReorder(
        tg.id,
        task.id,
        reorderedSubtasks.map((subtask) => subtask.id),
      );
      resetSubtaskDrag();
    },
    [
      canReorderSubtasks,
      getDropPosition,
      onSubtaskReorder,
      resetSubtaskDrag,
      subtaskDropIndicator,
      task.id,
      tg.id,
    ],
  );

  return (
    <>
      <tr
        className={clsx(
          "hover:bg-[#ECE9EE] border-b-[1px] z-50 border-[#78797A] cursor-pointer",
          {
            "opacity-60": isDragging,
          },
        )}
        style={{
          borderLeft: "6px solid #3B0069",
          boxShadow: rowDropShadow,
        }}
        data-scroll-anchor={`task-${task.id}`}
        onDragOver={(event) => onReorderDragOver?.(event, task.id)}
        onDrop={(event) => onReorderDrop?.(event, task.id)}
      >
        {bulkEditGroupId === tg.id && (
          <td className="px-0 py-2 w-[40px] text-center">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[#3B0069]"
              checked={selectedTaskIds.includes(task.id)}
              onChange={() => toggleTaskSelected(task.id)}
              onClick={(event) => event.stopPropagation()}
            />
          </td>
        )}

        <td
          className={`pl-${bulkEditGroupId === tg.id ? "0" : "2"} pr-1 py-0 z-30 w-[220px] xl:w-[280px] font-normal group`}
          onClick={openTaskDetails}
          id={`task-row-${task.id}`}
        >
          <div className="flex items-center w-full">
            <button
              type="button"
              draggable={canReorderTask}
              aria-label={`Reorder ${task.name}`}
              title={canReorderTask ? "Drag to reorder" : undefined}
              className={clsx("mr-1 rounded p-[1px] text-gray-400", {
                "cursor-grab hover:bg-gray-200 hover:text-gray-600 active:cursor-grabbing":
                  canReorderTask,
                "opacity-0 pointer-events-none": !canReorderTask,
              })}
              onClick={(event) => event.stopPropagation()}
              onDragStart={(event) => onReorderDragStart?.(event, task.id)}
              onDragEnd={onReorderDragEnd}
            >
              <svg
                viewBox="0 0 16 16"
                className="h-4 w-4 xl:h-[18px] xl:w-[18px]"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="5" cy="4" r="1.2" />
                <circle cx="11" cy="4" r="1.2" />
                <circle cx="5" cy="8" r="1.2" />
                <circle cx="11" cy="8" r="1.2" />
                <circle cx="5" cy="12" r="1.2" />
                <circle cx="11" cy="12" r="1.2" />
              </svg>
            </button>

            <button
              className="mr-1 expand-arrow rounded-full p-[0.5px] hover:bg-gray-300"
              onClick={(event) => {
                event.stopPropagation();
                onToggleTask(event, tg.id, task.id);
              }}
              onMouseDown={(event) => event.preventDefault()}
              id={`task-${task.id}`}
            >
              <svg
                className={clsx("w-3 h-3 xl:w-4 xl:h-4 transform", {
                  "rotate-90": task.expanded,
                })}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M9 5l7 7-7 7" strokeWidth={2} strokeLinecap="round" />
              </svg>
            </button>

            <div className="flex flex-row items-center justify-between w-[75%]">
              {editingTaskId === task.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitTaskName();
                  }}
                  className="flex items-center w-full overflow-hidden"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <input
                    autoFocus
                    ref={inputRef}
                    defaultValue={task.name}
                    maxLength={45}
                    onChange={(event) => {
                      pendingTaskNameRef.current = event.target.value;
                    }}
                    onBlur={(event) => {
                      event.preventDefault();
                      submitTaskName();
                    }}
                    className="pl-2 text-sm xl:text-[16px] mr-2 border border-purple-300 rounded"
                  />
                  <button
                    type="button"
                    onClick={onTaskEditCancel}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </form>
              ) : (
                <div className="cursor-pointer flex flex-col text-wrap">
                  <div className="inline-flex items-center">
                    <span
                      onClick={(event) => {
                        event.stopPropagation();
                        if (taskAuthority) {
                          closeAddForms();
                          preserveScroll(
                            `[data-scroll-anchor="task-${task.id}"]`,
                            () => onTaskEditStart(task),
                          );
                        } else {
                          openTaskDetails();
                        }
                      }}
                      className={`truncate text-wrap flex-wrap px-1 rounded-md hover:text-purple-600 text-sm xl:text-base ${
                        taskAuthority
                          ? "hover:outline hover:outline-1 hover:outline-black"
                          : ""
                      }`}
                    >
                      {task.name}
                    </span>
                  </div>

                  {task.expanded &&
                  Array.isArray(task.subtasks) &&
                  task.subtasks.length > 0 ? (
                    <div className="text-xs text-gray-500 whitespace-nowrap text-left">
                      {
                        task.subtasks.filter((subtask) =>
                          isSubtaskCompleted(subtask),
                        ).length
                      }
                      /{task.subtasks.length} subtasks complete
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <button
              className="p-1 hover:bg-gray-200 w-6 xl:w-8 rounded-full ml-1 xl:ml-2 hidden group-hover:block"
              onClick={(event) => {
                event.stopPropagation();
                openTaskDetails();
              }}
            >
              <CgPushRight size={18} className="xl:size-[22px]" color="black" />
            </button>
          </div>
        </td>

        <td className="px-1 xl:px-2 py-2 text-center w-[50px] xl:w-[60px]">
          {task?.subtasks_summary?.total_subtasks > 0 && (
            <span className="rounded-full bg-purple-200 text-[8px] xl:text-[12px] py-0.5 px-1.5 text-[#3B0069] font-medium">
              {task.subtasks_summary.completed_subtasks} /{" "}
              {task.subtasks_summary.total_subtasks}
            </span>
          )}
        </td>

        <td className="px-1 xl:px-2 py-2 text-center w-[130px] xl:w-[170px]">
          <div
            className="flex flex-row items-center justify-start w-full px-2 xl:px-[20px]"
            onClick={() => {
              preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, () =>
                setShowMemberModal((current) => !current),
              );
            }}
          >
            {Array.isArray(task.members) && task.members.length ? (
              <>
                {task.members.slice(0, 3).map((member) => {
                  const siteMember =
                    siteMembers.find(
                      (candidate) => candidate.member_id === member.member_id,
                    ) || {};
                  const avatarUrl = siteMember.userpic || member.userpic;

                  return avatarUrl ? (
                    <img
                      key={member.member_id}
                      src={avatarUrl}
                      alt={`${member.first_name} ${member.last_name}`}
                      className="w-6 h-6 xl:w-8 xl:h-8 rounded-full border-2 border-white"
                      style={{
                        objectFit: "cover",
                      }}
                      title={`${member.first_name} ${member.last_name}`}
                    />
                  ) : (
                    <span
                      key={member.member_id}
                      className="flex flex-row items-center justify-center w-6 h-6 xl:w-8 xl:h-8 rounded-full bg-[#343C6A] text-white font-medium text-[10px] xl:text-sm border-2 border-white"
                      title={`${member.first_name} ${member.last_name}`}
                    >
                      {getInitials(`${member.first_name} ${member.last_name}`)}
                    </span>
                  );
                })}
                {task.members.length > 3 && (
                  <span
                    className="flex flex-row items-center justify-center w-6 h-6 xl:w-8 xl:h-8 rounded-full bg-[#343C6A] text-white font-medium text-[10px] xl:text-sm border-2 border-white"
                    title={`+${task.members.length - 3} more members`}
                  >
                    +{task.members.length - 3}
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-500">Unassigned</span>
            )}
          </div>

          {showMemberModal && (
            <AddTaskMemberModal
              task={task}
              siteMembers={siteMembers}
              getInitials={getInitials}
              preserveScroll={preserveScroll}
              onMembersAdded={async (members) => {
                await refetchTasks(task.task_group);
                mergeGroupAllocatedMembers?.(task.task_group, members);
              }}
              onClose={() => setShowMemberModal(false)}
            />
          )}
        </td>

        <td
          className="px-1 xl:px-2 py-2 text-center w-[110px] xl:w-[150px] relative"
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, () =>
              setIsCalendarOpen(true),
            );
          }}
        >
          {task.dueDate ? (
            task.dueDate
          ) : (
            <div className="flex flex-row items-center justify-center w-full">
              <span className="text-xs xl:text-sm bg-purple-100 text-purple-700 px-1 xl:px-2 py-1 rounded-md hover:bg-purple-200">
                Select date
              </span>
            </div>
          )}

          <CalendarModal
            isVisible={isCalendarOpen}
            startDate={task.start_date}
            endDate={task.end_date}
            onClose={() => setIsCalendarOpen(false)}
            onDateChange={(startDate, endDate) => {
              preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, () => {
                const result = handleTaskDateChange(
                  task.id,
                  startDate,
                  endDate,
                );
                setIsCalendarOpen(false);
                return result;
              });
            }}
          />
        </td>

        <td className="relative px-1 xl:px-2 py-2 w-[110px] xl:w-[150px]">
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, () =>
                setOpenStatusDropdown(
                  openStatusDropdown === task.id ? null : task.id,
                ),
              );
            }}
            className="w-full px-1"
          >
            {task?.dueDate && (
              <StatusBadge value={getTaskDisplayStatus(task)} />
            )}
          </button>

          {openStatusDropdown === task.id && (
            <div
              className="absolute top-full px-2 py-2 shadow-xl shadow-[#00000040] left-1/2 -translate-x-1/2 z-20 w-48 bg-white rounded-xl border flex flex-col mt-1"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="text-center text-gray-700 font-semibold py-2 border-b-[1px] border-[#78797A] mb-2">
                Change status
              </div>
              {statusOptions.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className="text-lg font-semibold mb-2"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={(event) => {
                    event.stopPropagation();
                    preserveScroll(
                      `[data-scroll-anchor="task-${task.id}"]`,
                      () => {
                        const result = onTaskStatusChange(
                          tg.id,
                          task.id,
                          option.label,
                        );
                        setOpenStatusDropdown(null);
                        return result;
                      },
                    );
                  }}
                >
                  <StatusBadge value={option.label} />
                </button>
              ))}
            </div>
          )}
        </td>

        <td className="relative py-2 w-[100px] xl:w-[140px]">
          <div
            className="w-full px-1 xl:px-2"
            style={{
              alignSelf: "center",
            }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              preserveScroll(`[data-scroll-anchor="task-${task.id}"]`, () =>
                setOpenPriorityDropdown(
                  openPriorityDropdown === task.id ? null : task.id,
                ),
              );
            }}
          >
            <PriorityBadge value={task.priority} />
          </div>

          {openPriorityDropdown === task.id && (
            <div
              className="absolute top-full px-2 py-2 shadow-xl shadow-[#00000040] left-1/2 -translate-x-1/2 z-20 w-48 bg-white rounded-xl border flex flex-col mt-1"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="text-center text-gray-700 font-semibold py-2 border-b-[1px] border-[#78797A] mb-2">
                Change priority
              </div>
              {["High", "Medium", "Low"].map((option) => (
                <button
                  key={option}
                  className="text-lg font-semibold mb-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    preserveScroll(
                      `[data-scroll-anchor="task-${task.id}"]`,
                      () => {
                        const result = onTaskPriorityChange(
                          tg.id,
                          task.id,
                          priorityToNumeric[option],
                        );
                        setOpenPriorityDropdown(null);
                        return result;
                      },
                    );
                  }}
                >
                  <StatusBadge value={option} />
                </button>
              ))}
            </div>
          )}
        </td>

        <td className="px-1 xl:px-2 py-2 text-center w-[110px] xl:w-[150px]">
          {task?.dueDate && (
            <div
              className={clsx(
                "rounded-md px-1 xl:px-3 py-1 justify-end flex flex-row items-center font-medium text-[10px] xl:text-xs",
                mapDurationTextClass(task),
              )}
              style={{
                gap: "5px",
                background: mapBackground(task),
              }}
            >
              {renderTextForDue(task)}
              <div className="rounded-full bg-white p-0.5 xl:p-1 text-[#343C6A] font-semibold">
                {renderDays(task)}D
              </div>
            </div>
          )}
        </td>
      </tr>

      {task.expanded && (
        <tr>
          <td
            colSpan={bulkEditGroupId === tg.id ? 8 : 7}
            className="p-0 bg-[#F0F2F5] border-l-[6px] mb-2 border-[#F0F2F5]"
          >
            <div
              className="mt-2 mb-2 relative rounded-lg"
              style={{
                paddingLeft: bulkEditGroupId === tg.id ? "10%" : "8%",
              }}
            >
              <table
                className="w-full bg-white"
                style={{
                  borderLeft: "4px solid #3B0069",
                  borderRadius: "0.5rem",
                }}
              >
                <tbody>
                  {orderedSubtasks.map((subtask) => (
                    <SubtaskRow
                      key={subtask.id}
                      tg={tg}
                      task={task}
                      st={subtask}
                      taskAuthority={taskAuthority}
                      editingSubId={editingSubId}
                      onSubEditStart={onSubEditStart}
                      onSubEditCancel={onSubEditCancel}
                      onSubEditSubmit={onSubEditSubmit}
                      handleSubtaskDateChange={handleSubtaskDateChange}
                      onToggleSubComplete={onToggleSubComplete}
                      setSelectedTaskGroup={setSelectedTaskGroup}
                      setSelectedTask={setSelectedTask}
                      setSelectedSubtask={setSelectedSubtask}
                      getInitials={getInitials}
                      preserveScroll={preserveScroll}
                      refetchSubtasks={refetchSubtasks}
                      mergeGroupAllocatedMembers={mergeGroupAllocatedMembers}
                      closeAddForms={closeAddForms}
                      canReorderSubtask={canReorderSubtasks}
                      isDragging={
                        String(draggingSubtaskId) === String(subtask.id)
                      }
                      dropIndicatorPosition={
                        subtaskDropIndicator?.subtaskId === subtask.id
                          ? subtaskDropIndicator.position
                          : null
                      }
                      onReorderDragStart={handleSubtaskDragStart}
                      onReorderDragEnd={resetSubtaskDrag}
                      onReorderDragOver={handleSubtaskDragOver}
                      onReorderDrop={handleSubtaskDrop}
                    />
                  ))}
                  {task.subtasks.length === 0 && (
                    <tr className="border-b-[1px] border-b-[#78797A]">
                      <td
                        colSpan={6}
                        className="px-2 py-2 text-sm text-gray-700 text-left"
                      >
                        No Subtasks
                      </td>
                    </tr>
                  )}

                  {taskAuthority && (
                    <tr className="border-b-[1px] border-b-[#78797A]">
                      {showAddSubtaskModal === task.id ? (
                        <td
                          colSpan={6}
                          className="px-0 py-2 text-left text-sm text-gray-700 border-l-4 border-l-[#3B006966]"
                        >
                          <form
                            onSubmit={handleAddSubtaskSubmit}
                            className="flex w-full px-2 items-center relative"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="relative flex-1">
                              <input
                                autoFocus
                                type="text"
                                value={subtaskInputValue}
                                onChange={(event) =>
                                  setSubtaskInputValue(event.target.value)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Escape") {
                                    event.preventDefault();
                                    setShowAddSubtaskModal(null);
                                    setSubtaskInputValue("");
                                  }
                                }}
                                placeholder="New Subtask Name"
                                maxLength={65}
                                className="pl-2 py-1 w-[95%] text-xs xl:text-[16px] mr-2 border border-purple-300 rounded focus:ring-2 focus:ring-purple-500"
                              />
                              {subtaskInputValue.length >= 55 && (
                                <div className="absolute -bottom-6 left-0 text-xs text-gray-500">
                                  {subtaskInputValue.length}/65 characters
                                </div>
                              )}
                            </div>
                            <button
                              type="submit"
                              onMouseDown={(event) => event.preventDefault()}
                              disabled={!subtaskInputValue.trim()}
                              className={`text-purple-600 hover:text-purple-800 ${
                                !subtaskInputValue.trim()
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                setShowAddSubtaskModal(null);
                                setSubtaskInputValue("");
                              }}
                              className="text-gray-500 hover:text-gray-700 ml-2"
                            >
                              ✕
                            </button>
                          </form>
                        </td>
                      ) : (
                        <>
                          <td className="px-0 py-2 text-left text-sm text-gray-700 border-l-4 border-l-[#3B006966]">
                            <button
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                preserveScroll(
                                  `[data-scroll-anchor="task-${task.id}"]`,
                                  () => {
                                    onAddSubtaskClick(
                                      tg.id,
                                      task.id,
                                      task.dueDate,
                                    );
                                    setShowAddTaskModal(null);
                                  },
                                );
                              }}
                              className="text-purple-600 px-3 hover:text-purple-800 flex items-center"
                            >
                              ➕ Add Subtask
                            </button>
                          </td>
                          <td colSpan={5} />
                        </>
                      )}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const areEqual = (previousProps, nextProps) => {
  const isTaskEditAffected =
    previousProps.editingTaskId === previousProps.task.id ||
    nextProps.editingTaskId === nextProps.task.id;
  const isStatusDropdownAffected =
    previousProps.openStatusDropdown === previousProps.task.id ||
    nextProps.openStatusDropdown === nextProps.task.id;
  const isPriorityDropdownAffected =
    previousProps.openPriorityDropdown === previousProps.task.id ||
    nextProps.openPriorityDropdown === nextProps.task.id;
  const isSubtaskFormAffected =
    previousProps.showAddSubtaskModal === previousProps.task.id ||
    nextProps.showAddSubtaskModal === nextProps.task.id;
  const isBulkSelectionAffected =
    previousProps.bulkEditGroupId === previousProps.tg.id ||
    nextProps.bulkEditGroupId === nextProps.tg.id;

  return (
    previousProps.tg === nextProps.tg &&
    previousProps.task === nextProps.task &&
    previousProps.siteMembers === nextProps.siteMembers &&
    previousProps.taskAuthority === nextProps.taskAuthority &&
    previousProps.taskReorderDisabled === nextProps.taskReorderDisabled &&
    previousProps.canReorderTask === nextProps.canReorderTask &&
    previousProps.isDragging === nextProps.isDragging &&
    previousProps.dropIndicatorPosition === nextProps.dropIndicatorPosition &&
    (!isTaskEditAffected ||
      previousProps.editingTaskId === nextProps.editingTaskId) &&
    (!isStatusDropdownAffected ||
      previousProps.openStatusDropdown === nextProps.openStatusDropdown) &&
    (!isPriorityDropdownAffected ||
      previousProps.openPriorityDropdown === nextProps.openPriorityDropdown) &&
    (!isSubtaskFormAffected ||
      previousProps.showAddSubtaskModal === nextProps.showAddSubtaskModal) &&
    (!isBulkSelectionAffected ||
      (previousProps.bulkEditGroupId === nextProps.bulkEditGroupId &&
        previousProps.selectedTaskIds === nextProps.selectedTaskIds))
  );
};

const TaskBlock = memo(TaskBlockComponent, areEqual);

export default TaskBlock;
